import React, { useState } from 'react';
import {
  CarouselSaleCard,
  DiscountBadge,
  DiscountBadgeVariant,
  SaleCardVariant,
} from '@secretescapes/cauldron';
import {
  Button,
  Device,
  FormattedText,
  IconName,
  Lightbox,
  Scale,
  TooltipPosition,
  Typography,
  TypographyVariant,
  defaultHtmlMapping,
  useDeviceViewport,
} from '@secretescapes/falcon';
import { i18n } from '@secretescapes/i18n';
import { ROOM_IMAGE_DIMENSIONS, mapPhoto, mapToGalleryElement } from '@utils/image.utils';
import {
  getFormattedPriceWithBookingFee,
  getOfferLeadPriceLabelling,
  getOfferTotalPriceLabelling,
} from '@utils/pricing.utils';
import { useApplicationState } from '@utils/useApplicationState';
import { BullfinchFeatureFlags, useFeatureFlag } from '@utils/useFeatureFlag';
import { useModal } from '@utils/useModal';
import { useTranslations } from '@utils/useTranslations';
import { notEmpty } from '@features/AvailabilityPresentation/AvailabilityPresentation.utils';
import { LoadingSkeletonSection } from '@features/AvailabilityPresentation/LoadingSkeleton/LoadingSkeleton';
import { OfferCardInfo } from '@features/AvailabilityPresentation/Offers/OfferCardInfo/OfferCardInfo';
import { InfoBoxType, disableOffer } from '@features/AvailabilityPresentation/Offers/Offers.utils';
import {
  ControlledDetails,
  DetailsSection,
  UncontrolledDetails,
} from '@features/Sale/Details/UncontrolledDetails';
import { ExtendedOffer } from '../Offers.interfaces';
import { RefundabilityBadge } from '../RefundabilityBadge';
import {
  Actions,
  BookablePricing,
  Card,
  Content,
  Header,
  ImageSkeleton,
  LeadRatePriceValue,
  List,
  OccupancyContainer,
  PolicyContainer,
  Pricing,
  RefundabilityBadgeWrapper,
  StyledDiscountBadge,
  Tile,
} from './OfferCard.styles';

interface Props {
  offer: ExtendedOffer;
  onBookButtonClicked: (offerId: string) => void;
  availableRoomCount?: number;
  finalRate: string;
  finalRatePerRoomPerNight: string;
  openCalendar: (offerId: string) => void;
  infoBoxType: InfoBoxType | null;
  description: string;
  flightsSelected: boolean;
  showPhotos: boolean;
  isBookable: boolean;
  isRefundableCheckInDate?: boolean;
  numberOfTravellers: number;
  numberOfNights: number;
  numberOfRooms: number;
  showLabelDisclaimer: boolean;
}

export const OfferCard = ({
  offer,
  onBookButtonClicked,
  finalRate,
  finalRatePerRoomPerNight,
  availableRoomCount,
  openCalendar,
  infoBoxType,
  description,
  flightsSelected,
  showPhotos,
  isBookable,
  isRefundableCheckInDate,
  numberOfTravellers,
  numberOfNights,
  numberOfRooms,
  showLabelDisclaimer,
}: Props) => {
  const { title, details, room, pricing, cancellationInfo } = offer;
  const { info, photos } = room ?? {};
  const [isRoomDetailsOpen, setIsRoomDetailsOpen] = useState(false);

  const [focusedImageIndex, setFocusedImageIndex] = useState(0);
  const {
    isModalOpen: showLightbox,
    openModal: openLightbox,
    closeModal: closeLightbox,
  } = useModal();

  const { currency, territoryLocale } = useApplicationState();
  const { translations } = useTranslations();
  const deviceViewport = useDeviceViewport();
  const isDesktop = deviceViewport === Device.desktop;

  const cardImages =
    photos?.filter(notEmpty).map(photo => mapPhoto(photo, ROOM_IMAGE_DIMENSIONS[deviceViewport])) ??
    [];

  const lightboxImages =
    photos?.filter(notEmpty).map((photo, index) => mapToGalleryElement(photo, index)) ?? [];

  const [showSliderNavigation, setShowSliderNavigation] = React.useState<boolean>(false);

  const bookingFeesEnabled = useFeatureFlag(BullfinchFeatureFlags.bookingFeesEnabled);
  const offerCardDetailsEnabled = useFeatureFlag(BullfinchFeatureFlags.offerCardDetailsEnabled);

  const handleImageClick = (index: number) => {
    setFocusedImageIndex(index);
    openLightbox();
  };

  // Apply booking fee if HO and FF on
  const leadRate =
    flightsSelected || !bookingFeesEnabled
      ? (pricing?.leadRate?.amountForDisplay ?? '')
      : getFormattedPriceWithBookingFee(
          Math.ceil(pricing?.leadRate?.amount ?? 0),
          currency,
          territoryLocale
        );

  const showInfoBox =
    infoBoxType === InfoBoxType.NOT_ENOUGH_ROOMS ||
    infoBoxType === InfoBoxType.UNSUITABLE_ROOM_FOR_TRAVELLERS ||
    infoBoxType === InfoBoxType.UNAVAILABLE_FOR_DATES;

  return (
    <>
      <Tile
        data-testid="offer-card"
        onMouseEnter={isDesktop ? () => setShowSliderNavigation(true) : undefined}
        onMouseLeave={isDesktop ? () => setShowSliderNavigation(false) : undefined}
        isBookable={isBookable}
      >
        {showPhotos && (
          <CarouselSaleCard
            images={cardImages}
            variant={SaleCardVariant.normal}
            showSliderNavigation={showSliderNavigation}
            showDotsNavigation={showSliderNavigation}
            onImageClick={handleImageClick}
          />
        )}
        <Card isDisabled={disableOffer(infoBoxType) && !isRoomDetailsOpen}>
          <Header>
            <Typography variant={TypographyVariant.t5SansBold}>{title ?? ''}</Typography>
            <OccupancyContainer>
              <ControlledDetails
                buttonLabel={i18n('sale.booking.roomCapacity.label', [], translations)}
                popupTitle={i18n('sale.booking.roomCapacity.label', [], translations)}
                labelIcon={IconName.people}
                isOpen={isRoomDetailsOpen}
                setIsOpen={setIsRoomDetailsOpen}
                withPortal={true}
                content={
                  <PolicyContainer>
                    <DetailsSection
                      header={i18n('sale.booking.childPolicy.label', [], translations)}
                      description={info?.childPolicy ?? ''}
                      id="childPolicy"
                    />
                    <DetailsSection
                      header={i18n('sale.booking.singleTraveller.label', [], translations)}
                      description={info?.singlePolicy ?? ''}
                      id="singlePolicy"
                    />
                  </PolicyContainer>
                }
              />
              {offerCardDetailsEnabled && (
                <UncontrolledDetails
                  buttonLabel={i18n('sale.booking.offerDetails.label', [], translations)}
                  labelIcon={IconName.info}
                  content={
                    <>
                      <List>
                        <FormattedText html={description} />
                      </List>
                      <br />
                      <FormattedText
                        html={details?.longDescription ?? ''}
                        htmlMapping={{
                          ...defaultHtmlMapping,
                          a: TypographyVariant.link,
                        }}
                      />
                    </>
                  }
                  popupTitle={i18n('sale.booking.offerDetails.label', [], translations)}
                />
              )}
            </OccupancyContainer>
            {cancellationInfo && (
              <RefundabilityBadgeWrapper>
                <RefundabilityBadge
                  isFlightOptionSelected={flightsSelected}
                  isRefundableCheckInDate={isRefundableCheckInDate}
                  cancellationInfo={cancellationInfo}
                />
              </RefundabilityBadgeWrapper>
            )}
          </Header>
          {!offerCardDetailsEnabled && (
            <Content>
              <List>
                <FormattedText html={description} />
              </List>
              <UncontrolledDetails
                buttonLabel={i18n('sale.booking.offerDetails.label', [], translations)}
                content={details?.longDescription ?? ''}
                popupTitle={i18n('sale.booking.offerDetails.label', [], translations)}
              />
            </Content>
          )}
          <Actions>
            {isBookable ? (
              <BookablePricing>
                <LeadRatePriceValue
                  translationData={getOfferTotalPriceLabelling({
                    unitPrice: finalRatePerRoomPerNight,
                    numberOfTravellers,
                    numberOfNights,
                    numberOfRooms,
                    roomType: offer.room?.roomType ?? '',
                    roomTypePlural: offer?.room?.roomTypePlural ?? '',
                    flightsSelected,
                    translations,
                  })}
                  leadRate={finalRate}
                  rackRate=""
                  showRackRate={false}
                  showTooltip={false}
                />
                {infoBoxType === InfoBoxType.LOW_ROOMS && (
                  <OfferCardInfo
                    infoBoxType={InfoBoxType.LOW_ROOMS}
                    availableRoomCount={availableRoomCount}
                  />
                )}
              </BookablePricing>
            ) : pricing && leadRate ? (
              <Pricing>
                <LeadRatePriceValue
                  translationData={getOfferLeadPriceLabelling({
                    flightsSelected,
                    numberOfTravellers,
                    roomType: offer.room?.roomType ?? '',
                    showDisclaimer: showLabelDisclaimer,
                    translations,
                  })}
                  leadRate={leadRate}
                  rackRate={pricing.rackRate?.amountForDisplay ?? ''}
                  showRackRate={!!pricing.showRackRate}
                  showTooltip={false}
                  tooltipPosition={TooltipPosition.bottomRight}
                />
                {pricing.showDiscount ? (
                  <StyledDiscountBadge>
                    <DiscountBadge
                      discountPrice={`${pricing.discount ?? ''}`}
                      variant={DiscountBadgeVariant.default}
                      discountTooltip={pricing.discountTooltip ?? ''}
                      showTooltip={!!pricing.discountTooltip}
                    />
                  </StyledDiscountBadge>
                ) : null}
              </Pricing>
            ) : null}
            {showInfoBox && !isBookable && (
              <OfferCardInfo
                infoBoxType={infoBoxType}
                onUnavailableForDatesClick={() => openCalendar(offer.id)}
                onUnsuitableForTravellersClick={() => setIsRoomDetailsOpen(!isRoomDetailsOpen)}
              />
            )}
            {isBookable ? (
              <Button
                dataAttributes={{ testid: 'book-button' }}
                onClick={() => onBookButtonClicked(offer.id)}
              >
                {i18n(
                  'offer.cta.label',
                  [
                    numberOfRooms,
                    numberOfRooms > 1
                      ? (offer.room?.roomTypePlural ?? '')
                      : (offer.room?.roomType ?? ''),
                  ],
                  translations
                )}
              </Button>
            ) : null}
          </Actions>
        </Card>
      </Tile>
      {showLightbox && (
        <Lightbox
          dataAttributes={{ testid: 'room-images-lightbox' }}
          closeButtonText={i18n('common.close.label', [], translations)}
          images={lightboxImages}
          infiniteCarousel={true}
          initialSlide={focusedImageIndex}
          onClose={closeLightbox}
          showNavigation={true}
          animation={true}
        />
      )}
    </>
  );
};
interface SkeletonProps {
  showButton: boolean;
  showDetailsContent: boolean;
  showPhoto: boolean;
}

const Skeleton = ({ showButton, showDetailsContent, showPhoto }: SkeletonProps) => {
  const commonLoadingStyleProps = {
    height: Scale.l,
    borderRadius: Scale.xxs,
  };

  return (
    <Tile isBookable={showButton} data-testid="offer-card-skeleton">
      {showPhoto && (
        <div>
          <ImageSkeleton {...commonLoadingStyleProps} />
        </div>
      )}
      <Card isDisabled={false}>
        <Header>
          <LoadingSkeletonSection {...commonLoadingStyleProps} />
          <OccupancyContainer>
            <LoadingSkeletonSection {...commonLoadingStyleProps} width="45%" />
          </OccupancyContainer>
          <RefundabilityBadgeWrapper>
            <LoadingSkeletonSection {...commonLoadingStyleProps} width="45%" />
          </RefundabilityBadgeWrapper>
        </Header>
        {showDetailsContent && (
          <Content>
            <LoadingSkeletonSection {...commonLoadingStyleProps} />
            <LoadingSkeletonSection {...commonLoadingStyleProps} />
            <LoadingSkeletonSection {...commonLoadingStyleProps} />
            <LoadingSkeletonSection {...commonLoadingStyleProps} />
            <LoadingSkeletonSection {...commonLoadingStyleProps} />
          </Content>
        )}
        <Actions>
          <Pricing>
            <LoadingSkeletonSection height="61px" width="45%" borderRadius={Scale.xxs} />
            {!showButton && (
              <LoadingSkeletonSection height="61px" width="20%" borderRadius={Scale.xxs} />
            )}
          </Pricing>
          {showButton && <LoadingSkeletonSection height="50px" borderRadius={Scale.xxs} />}
        </Actions>
      </Card>
    </Tile>
  );
};

OfferCard.Skeleton = Skeleton;
